import React, { useEffect, useRef, useState } from 'react';
import { request } from 'cademy-shared-web';
import { selectIsAuthenticated } from 'selectors';
import { useAppSelector } from 'store';
import { Environment } from './Environment';

export const CoreSocketContext = React.createContext({
    socket: null,
    addListener: () => {},
    removeListener: () => {},
    trigger: () => {},
    sendMessage: () => {},
});

const SocketContext = ({ children }) => {
    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const listeners = useRef([]);
    const socket = useRef(null);
    const ticker = useRef(null);

    const addListener = (action, func) => {
        if (!listeners.current[action]) listeners.current[action] = [];
        if (!listeners.current[action].some((f) => f === func))
            listeners.current[action].push(func);
    };

    const removeListener = (action, func) => {
        if (listeners.current[action]) {
            listeners.current[action] = listeners.current[action].filter((f) => f !== func);
        }
    };

    const trigger = ({ action, data }) => {
        if (listeners.current[action]) {
            listeners.current[action].forEach((func) => {
                try {
                    func(data);
                } catch (error) {
                    console.error('Error in listener', error);
                }
            });
        }
    };

    const sendMessage = (action, data) => {
        if (socket.current) {
            const message = JSON.stringify({ action, data });
            console.debug('sending message', message);
            socket.current.send(message);
        } else {
            console.error('Socket not connected');
        }
    };

    const [socketClient, setSocketClient] = useState({
        addListener,
        removeListener,
        sendMessage,
        online: false,
    });

    useEffect(() => {
        let ws = null;

        if (isAuthenticated) {
            try {
                ws = new WebSocket(Environment.SOCKET_URL);
                ws.onopen = async () => {
                    console.debug('socket open');
                    socket.current = ws;
                    setSocketClient({
                        ...socketClient,
                        online: true,
                    });
                    // Keep Alive Ping - Socket will be killed if > 10m of idle
                    ticker.current = setInterval(() => {
                        sendMessage('ping', null);
                    }, 60000); // 1 minute

                    try {
                        const { data } = await request.get('/sockets/authenticate');
                        sendMessage('auth-request', { token: data.token });
                    } catch (error) {
                        console.error(error);
                        ws.close();
                    }
                };
                ws.onmessage = (event) => {
                    console.debug('socket message', event);
                    const { action, data } = JSON.parse(event.data);

                    trigger({ action, data });

                    // if (action === 'notification') {
                    //     dispatch({ type: NOTIFICATION_RECEIVED, payload: data });
                    // } else {
                    //     console.debug('Unknown action', action, data);
                    // }
                };

                ws.onerror = (error) => {
                    console.debug('socket error', error);
                };

                ws.onclose = () => {
                    console.debug('socket close');
                    socket.current = null;

                    clearInterval(ticker.current);
                    ticker.current = null;

                    setSocketClient({
                        ...socketClient,
                        online: false,
                    });
                };
            } catch (error) {
                console.error('error', error);
            }

            return () => {
                console.debug('closing socket (SocketContext unmount)');
                if (socket.current) socket.current.close();
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    return <CoreSocketContext.Provider value={socketClient}>{children}</CoreSocketContext.Provider>;
};

export default SocketContext;
